/** Local draft envelope only. The API sends clean text and structured references
 * separately; this lets the existing per-chat drafts/queue/retry keep both intact. */
export type ReviewReference = {
  companyId: string;
  label: string;
} & ({ kind: "quotation"; id: string; version: number; contentHash: string }
  | { kind: "revision"; threadId: number; lastActivityId: number });

export interface Quotation {
  id: string; companyId: string; companyName: string; threadId: number;
  version: number; contentHash: string; title: string; description: string;
  amountSgd: number; capable: boolean;
  requirements: { owner: string; client: string };
  status: "draft" | "publishing" | "published" | "cancelling" | "cancelled" | "client_approved" | "client_declined" | "client_adjusted";
  portalUrl: string | null; createdAt: number | string; updatedAt: number | string;
  bridgeReady?: boolean;
  registrationChanged?: boolean;
  lastError?: string | null;
  notificationState?: string | null;
  pendingOperation?: { requestId: string; action: "approve" | "cancel"; state: "pending" | "blocked" };
}

/** Statuses from which nothing further can be decided — the record is finished. */
const TERMINAL_QUOTE_STATUS = ["cancelled", "published", "client_approved", "client_declined"];

export function quotationNeedsOwner(quote: Quotation): boolean {
  // A FINISHED quote never needs the owner again, no matter how its last operation ended.
  // Cancelling while the site was rejecting our signature left `cancel` stuck at "blocked",
  // and the blocked branch below then kept the cancelled record pinned above the composer
  // permanently — the owner cancelled it and it would not go away. The blocked flag is only
  // meaningful while the quote is still actionable.
  if (TERMINAL_QUOTE_STATUS.includes(quote.status)) return false;
  return ["draft", "client_adjusted"].includes(quote.status) || quote.pendingOperation?.state === "blocked";
}

const PREFIX = "/review-references ";
function validReference(value: unknown): value is ReviewReference {
  if (!value || typeof value !== "object") return false;
  const r = value as Record<string, unknown>;
  if (typeof r.companyId !== "string" || !r.companyId || typeof r.label !== "string" || !r.label || r.label.length > 220) return false;
  return r.kind === "quotation"
    ? typeof r.id === "string" && Number.isSafeInteger(r.version) && Number(r.version) > 0 && typeof r.contentHash === "string"
    : r.kind === "revision" && Number.isSafeInteger(r.threadId) && Number(r.threadId) > 0 && Number.isSafeInteger(r.lastActivityId);
}
export function reviewToken(ref: Pick<ReviewReference, "label">): string { return `⟦${ref.label}⟧`; }
export function parseReviewDraft(raw: string): { text: string; references: ReviewReference[] } {
  if (!raw.startsWith(PREFIX)) return { text: raw, references: [] };
  const end = raw.indexOf("\n\n");
  if (end < 0) return { text: raw, references: [] };
  try {
    const refs: unknown = JSON.parse(raw.slice(PREFIX.length, end));
    if (!Array.isArray(refs) || refs.length > 30 || !refs.every(validReference)) return { text: raw, references: [] };
    const text = raw.slice(end + 2);
    return { text, references: refs.filter(ref => text.includes(reviewToken(ref))) };
  } catch { return { text: raw, references: [] }; }
}
export function formatReviewDraft(text: string, references: ReviewReference[]): string {
  const active = references.filter(ref => text.includes(reviewToken(ref)));
  return active.length ? PREFIX + JSON.stringify(active) + "\n\n" + text : text;
}
export function referenceKey(r: ReviewReference): string {
  return r.kind === "quotation" ? `${r.companyId}:q:${r.id}:${r.version}` : `${r.companyId}:r:${r.threadId}:${r.lastActivityId}`;
}
export function appendReviewReference(raw: string, input: ReviewReference): string {
  const current = parseReviewDraft(raw);
  const same = current.references.find(r => referenceKey(r) === referenceKey(input));
  // Labels are display-only, unique inside this message. Record IDs never rely on them.
  const base = input.label.replace(/[⟦⟧\r\n]/g, " ").slice(0, 180);
  let label = base, i = 2;
  while (!same && current.references.some(r => r.label === label)) label = `${base} (${i++})`;
  const ref = same ?? { ...input, label };
  const text = current.text + (current.text.trim() ? "\n\n" : "") + reviewToken(ref) + " ";
  return formatReviewDraft(text, same ? current.references : [...current.references, ref]);
}
export function reviewTextParts(text: string, references: ReviewReference[]): Array<{ text: string; reference?: ReviewReference }> {
  const tokens = new Map(references.map(r => [reviewToken(r), r]));
  return text.split(/(⟦[^⟦⟧\n]+⟧)/g).filter(Boolean).map(part => ({ text: part, reference: tokens.get(part) }));
}
